import React from 'react';
import { Card, CardContent, Typography, Box, useTheme, Chip } from '@mui/material';
import { 
  SportsBasketball, 
  SportsTennis,
  Timer, 
  NotificationImportant, 
  SentimentDissatisfied,
  SportsTennisTwoTone 
} from '@mui/icons-material';

const GameCard = ({ game }) => {
  const theme = useTheme();

  // Get sport-specific icon/emoji
  const getSportIcon = (sport) => {
    switch (sport.toLowerCase()) {
      case 'nfl':
        return <Typography sx={{ 
          mr: 2, 
          fontSize: 28
        }}>
          🏈
        </Typography>;
      case 'ncaam':
      case 'ncaaw':
        return <SportsBasketball sx={{ 
          mr: 2, 
          color: theme.palette.primary.main,
          fontSize: 28
        }} />;
      case 'tennis':
        return <SportsTennis sx={{ 
          mr: 2, 
          color: theme.palette.primary.main,
          fontSize: 28
        }} />;
      default:
        return null;
    }
  };

  // Find viewing recommendations from summary
  const getViewingRecommendations = (summary) => {
    const recommendations = [];
    const unrecognizedLines = [];
    
    for (const line of summary) {
      // Check for DRAMA timestamp pattern (comma-delimited)
      const dramaMatch = line.match(/DRAMA, (\d+), (\d+)/);
      if (dramaMatch) {
        recommendations.push({
          type: 'drama',
          message: `Drama at minute ${dramaMatch[1]}`,
          icon: <Timer sx={{ mr: 1, color: theme.palette.warning.main }} />
        });
        continue;
      }
      
      // Check for CLOSE LATE pattern
      if (line.includes('CLOSE LATE')) {
        recommendations.push({
          type: 'close',
          message: 'It\'s close at the end of the game!',
          icon: <NotificationImportant sx={{ mr: 1, color: theme.palette.error.main }} />
        });
        continue;
      }

      // Check for BORING pattern
      if (line === 'BORING') {
        recommendations.push({
          type: 'boring',
          message: 'boring...',
          icon: <SentimentDissatisfied sx={{ mr: 1, color: theme.palette.grey[500] }} />
        });
        continue;
      }

      // Check for first decisive set pattern
      if (line.startsWith('first decisive set')) {
        recommendations.push({
          type: 'decisive',
          message: line,
          icon: <SportsTennisTwoTone sx={{ mr: 1, color: theme.palette.info.main }} />
        });
        continue;
      }

      // If we get here, this is an unrecognized line
      unrecognizedLines.push(line);
    }

    // Add unrecognized lines as plain text recommendations
    if (unrecognizedLines.length > 0) {
      recommendations.push({
        type: 'plain',
        message: unrecognizedLines.join('\n'),
        icon: null
      });
    }

    return recommendations;
  };

  const recommendations = getViewingRecommendations(game.summary);

  return (
    <Card 
      sx={{ 
        minWidth: 275,
        borderRadius: 2,
        boxShadow: theme.shadows[3],
        '&:hover': {
          transform: 'translateY(-2px)',
          transition: 'transform 0.2s ease-in-out',
          boxShadow: theme.shadows[6],
        },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center'
          }}>
            {getSportIcon(game.sport)}
            <Typography 
              variant="h5" 
              component="div"
              sx={{ 
                fontWeight: 600,
                color: theme.palette.text.primary
              }}
            >
              {game.awayTeam} @ {game.homeTeam}
            </Typography>
          </Box>
          <Chip 
            label={game.sport.toUpperCase()} 
            size="small"
            sx={{ 
              backgroundColor: theme.palette.primary.main,
              color: 'white',
              fontWeight: 500
            }}
          />
        </Box>
        
        {recommendations.length > 0 && (
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            mb: 2
          }}>
            {recommendations.map((rec, index) => (
              <Box 
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: rec.type !== 'plain' ? 'center' : 'flex-start',
                  p: 1.5,
                  backgroundColor: theme.palette.grey[100],
                  borderRadius: 1,
                  border: `1px solid ${theme.palette.grey[300]}`,
                }}
              >
                {rec.icon}
                <Typography 
                  sx={{
                    fontWeight: rec.type !== 'plain' ? 500 : 400,
                    color: theme.palette.text.primary,
                    whiteSpace: rec.type === 'plain' ? 'pre-line' : 'normal'
                  }}
                >
                  {rec.message}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        <Typography 
          sx={{ 
            mb: 2,
            color: theme.palette.text.secondary,
            fontSize: '0.875rem',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          Updated: {game.scrape_date.toDate().toLocaleString()}
        </Typography>
      </CardContent>
    </Card>
  );
};

export default GameCard; 